import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { ShieldCheck, Loader2 } from "lucide-react";
import { Modal } from "../../components/Modal";
import { stepUp, setStepUpProvider, ApiError } from "../../api";

/**
 * Global re-authentication gate. Registers itself as the step-up provider so
 * api() can, on a 403 STEP_UP_REQUIRED, pop this modal, collect the password,
 * exchange it for a short-lived step-up token, and transparently retry the
 * original request. Mounted once inside the authenticated app shell.
 *
 * Password only — no 2FA code. The active session already proves a full login
 * (including 2FA when enabled) happened; the backend re-checks just the
 * password. Once the session expires the user does a full re-login instead.
 */
export function StepUpGate() {
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Every pending api() call waiting on a token. Resolved together so
  // concurrent sensitive actions share a single prompt.
  const waiters = useRef<((t: string | null) => void)[]>([]);

  const settle = useCallback((token: string | null) => {
    const pending = waiters.current;
    waiters.current = [];
    setOpen(false);
    setPw(""); setErr(null); setBusy(false);
    pending.forEach((resolve) => resolve(token));
  }, []);

  useEffect(() => {
    setStepUpProvider(() => new Promise<string | null>((resolve) => {
      const first = waiters.current.length === 0;
      waiters.current.push(resolve);
      if (!first) return; // a prompt is already on screen — piggyback on it
      setPw(""); setErr(null); setBusy(false);
      setOpen(true);
    }));
    return () => setStepUpProvider(null);
  }, []);

  async function submit(e?: FormEvent) {
    e?.preventDefault();
    if (!pw) return;
    setBusy(true); setErr(null);
    try {
      const token = await stepUp(pw);
      settle(token);
    } catch (e2) {
      setErr(e2 instanceof ApiError ? e2.message : "Confirmation failed");
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => settle(null)}
      size="sm"
      title={<span className="stepup-title"><ShieldCheck className="icon-svg" /> Confirmation required</span>}
      footer={
        <>
          <button onClick={() => settle(null)} disabled={busy}>Cancel</button>
          <button
            onClick={() => submit()}
            className="btn-primary"
            disabled={busy || !pw}
          >
            {busy ? <Loader2 className="icon-svg spin" /> : <ShieldCheck className="icon-svg" />} Confirm
          </button>
        </>
      }
    >
      <form onSubmit={submit} className="stepup-form">
        <p className="muted">
          This action is security-relevant. Confirm it with your password.
        </p>
        <input
          type="password"
          placeholder="Password"
          value={pw}
          autoFocus
          autoComplete="current-password"
          onChange={(e) => { setPw(e.target.value); setErr(null); }}
        />
        {err && <p className="error">{err}</p>}
      </form>
    </Modal>
  );
}
